<template>
  <div
    class="relative rounded-full flex items-center justify-center flex-shrink-0"
    :class="[sizeClasses.wrapper, ringClass]"
    :style="wrapperStyle"
    role="img"
    :aria-label="ariaLabel"
  >
    <div
      v-if="isCoordinator && showCoordinatorStar"
      class="pointer-events-none absolute -top-0.5 -right-0.5 z-[2] flex items-center justify-center rounded-full bg-cyan-300 text-slate-950 ring-2 ring-slate-950/70"
      :class="sizeClasses.star"
      aria-hidden="true"
      title="Coordenador"
    >
      <svg class="w-full h-full p-[0.18rem]" viewBox="0 0 20 20" fill="currentColor">
        <path
          d="M10 1.8l2.45 5.04 5.56.78-4.04 3.9.98 5.5L10 14.82 5.05 17.02l.98-5.5-4.04-3.9 5.56-.78L10 1.8z"
        />
      </svg>
    </div>
    <svg
      viewBox="0 0 64 64"
      class="block"
      :class="sizeClasses.svg"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Corpo = cor do agente; contorno preto; painel facial com borda ciano. -->
      <g :stroke="ink" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round" fill="none">
        <!-- 1 Orbit: cabeça redonda (maior), antena, orelhas retangulares, rosto circular -->
        <g v-if="effectiveVariant === 'orbit'">
          <rect x="9.5" y="19.5" width="6" height="10.5" rx="1.2" :fill="displayColor" />
          <rect x="48.5" y="19.5" width="6" height="10.5" rx="1.2" :fill="displayColor" />
          <circle cx="32" cy="23.5" r="14" :fill="displayColor" />
          <line x1="32" y1="9.5" x2="32" y2="3.5" />
          <circle cx="32" cy="2.4" r="2" :fill="displayColor" />
          <!-- Painel maior e concêntrico à cabeça → faixa de cor mais fina -->
          <circle
            cx="32"
            cy="23.5"
            r="9.85"
            :fill="faceScreen"
            :stroke="accentCyan"
            stroke-width="0.9"
          />
          <g :class="eyesPulseClass">
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="24.85,21.85 26.25,20.65 28.15,20.65 29.55,21.85 28.15,23.05 26.25,23.05"
            />
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="34.45,21.85 35.85,20.65 37.75,20.65 39.15,21.85 37.75,23.05 35.85,23.05"
            />
          </g>
          <rect x="29.35" y="27.55" width="5.3" height="1.75" rx="0.35" :fill="accentCyan" stroke="none" />
          <rect x="27.5" y="36.2" width="9" height="3" rx="1" :fill="displayColor" />
          <!-- Corpo: meia-elipse (translate + ry afinados ao limite inferior do viewBox) -->
          <path
            transform="translate(0, 10)"
            d="M 10.8 41.5 L 53.2 41.5 A 21.2 11.5 0 0 0 10.8 41.5 Z"
            :fill="displayColor"
          />
        </g>

        <!-- 2 Deck: cabeça larga arredondada, duas antenas, orelhas grandes, “tela” retangular -->
        <g v-else-if="effectiveVariant === 'deck'">
          <rect x="5.5" y="20.5" width="6.5" height="13" rx="1.6" :fill="displayColor" />
          <rect x="52" y="20.5" width="6.5" height="13" rx="1.6" :fill="displayColor" />
          <rect x="10" y="16" width="44" height="20" rx="5.5" :fill="displayColor" />
          <line x1="21" y1="16" x2="21" y2="7" />
          <circle cx="21" cy="5.5" r="2" :fill="displayColor" />
          <line x1="43" y1="16" x2="43" y2="7" />
          <circle cx="43" cy="5.5" r="2" :fill="displayColor" />
          <rect
            x="15.5"
            y="20"
            width="33"
            height="12.5"
            rx="3.2"
            :fill="faceScreen"
            :stroke="accentCyan"
            stroke-width="0.9"
          />
          <g :class="eyesPulseClass">
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="22.4,25.2 23.9,23.9 26.1,23.9 27.6,25.2 26.1,26.5 23.9,26.5"
            />
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="36.4,25.2 37.9,23.9 40.1,23.9 41.6,25.2 40.1,26.5 37.9,26.5"
            />
          </g>
          <rect x="27.2" y="28.5" width="9.6" height="2.6" rx="0.5" :fill="accentCyan" stroke="none" />
          <rect x="26.5" y="36.2" width="11" height="3.2" rx="1" :fill="displayColor" />
          <path
            transform="translate(0, 10)"
            d="M 9.6 41.8 L 54.4 41.8 A 22.4 11.2 0 0 0 9.6 41.8 Z"
            :fill="displayColor"
          />
        </g>

        <!-- 3 Spire: retângulo + orelhas altas + antenas laterais + pescoço em dois blocos -->
        <g v-else-if="effectiveVariant === 'spire'">
          <rect x="21.5" y="3.5" width="5.5" height="15" rx="2.6" :fill="displayColor" />
          <rect x="37" y="3.5" width="5.5" height="15" rx="2.6" :fill="displayColor" />
          <line x1="11.5" y1="26" x2="11.5" y2="19" />
          <circle cx="11.5" cy="17.5" r="2.3" :fill="displayColor" />
          <line x1="52.5" y1="26" x2="52.5" y2="19" />
          <circle cx="52.5" cy="17.5" r="2.3" :fill="displayColor" />
          <rect x="15.5" y="19" width="33" height="18" rx="2.2" :fill="displayColor" />
          <rect
            x="19"
            y="23"
            width="26"
            height="10.5"
            rx="1.8"
            :fill="faceScreen"
            :stroke="accentCyan"
            stroke-width="0.9"
          />
          <g :class="eyesPulseClass">
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="23.2,26.5 24.5,25.4 26.2,25.4 27.5,26.5 26.2,27.6 24.5,27.6"
            />
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="36.5,26.5 37.8,25.4 39.5,25.4 40.8,26.5 39.5,27.6 37.8,27.6"
            />
          </g>
          <rect x="27.5" y="29.8" width="9" height="2.3" rx="0.45" :fill="accentCyan" stroke="none" />
          <rect x="25.75" y="37.2" width="12.5" height="3" rx="0.65" :fill="displayColor" />
          <rect x="25.75" y="40.6" width="12.5" height="3.1" rx="0.65" :fill="displayColor" />
          <path
            transform="translate(0, 10)"
            d="M 10.6 42.2 L 53.4 42.2 A 21.4 10.8 0 0 0 10.6 42.2 Z"
            :fill="displayColor"
          />
        </g>

        <!-- Coordenador 1: Hub Central (baseado no SVG enviado) -->
        <g v-else-if="effectiveVariant === 'coord_hub'">
          <g transform="translate(-14 -18) scale(0.47)">
            <line x1="100" y1="61" x2="100" y2="46" :stroke="accentCyan" stroke-width="2.5" />

            <circle cx="100" cy="99" r="38" :fill="displayColor" :stroke="ink" stroke-width="3" />
            <circle cx="100" cy="99" r="27.5" :fill="faceScreen" :stroke="accentCyan" stroke-width="3" />
            <g :class="eyesPulseClass">
              <circle cx="100" cy="99" r="4.5" :fill="accentCyan" />
            </g>

            <circle cx="100" cy="46" r="6.5" :fill="accentCyan" :stroke="ink" stroke-width="2" />

            <path d="M61 170c9-15 23-24 39-24s30 10 39 24" :fill="displayColor" :stroke="ink" stroke-width="2" />
          </g>
        </g>

        <!-- Coordenador 2: Supervisor (baseado no SVG enviado) -->
        <g v-else-if="effectiveVariant === 'coord_supervisor'">
          <g transform="translate(-14 -18) scale(0.47)">
            <!-- Cabeça (inclui visor/rosto) -->
            <g transform="translate(-5, 13) scale(1.03)">
              <path d="M70 73c0-14 12-25 30-25s30 11 30 25v28c0 11-9 20-20 20H90c-11 0-20-9-20-20z" :fill="displayColor" :stroke="ink" stroke-width="2.5" />
              <rect x="60" y="76" width="10" height="28" rx="3" :fill="displayColor" :stroke="ink" stroke-width="2" />
              <rect x="130" y="76" width="10" height="28" rx="3" :fill="displayColor" :stroke="ink" stroke-width="2" />

              <!-- Antena central -->
              <line x1="100" y1="51" x2="100" y2="31" :stroke="accentCyan" stroke-width="2.5" />
              <circle cx="100" cy="31.5" r="6.5" :fill="accentCyan" :stroke="ink" stroke-width="2" />

              <rect x="76" y="72" width="48" height="30" rx="8.5" :fill="faceScreen" :stroke="accentCyan" stroke-width="2.5" />
              <g :class="eyesPulseClass">
                <circle cx="90" cy="85" r="4" :fill="accentCyan" />
                <circle cx="110" cy="85" r="4" :fill="accentCyan" />
              </g>
              <path d="M90 94h20" :stroke="accentCyan" stroke-width="3" stroke-linecap="round" />

              <path d="M83 57c5-8 11-12 17-12s12 4 17 12" fill="none" :stroke="ink" stroke-width="2.5" stroke-linecap="round" />
              <rect x="87" y="45" width="26" height="7" rx="3.5" :fill="displayColor" :stroke="ink" stroke-width="2" />
            </g>

            <path d="M61 170c9-15 23-24 39-24s30 10 39 24" :fill="displayColor" :stroke="ink" stroke-width="2" />
          </g>
        </g>

        <!-- Coordenador 3: Orquestrador (baseado no SVG enviado) -->
        <g v-else-if="effectiveVariant === 'coord_orchestrator'">
          <g transform="translate(-14 -18) scale(0.47)">
            <g transform="translate(0, 11)">
              <rect x="62" y="62" width="76" height="60" rx="10" :fill="displayColor" :stroke="ink" stroke-width="2.5" />
              <rect x="70" y="72" width="60" height="36" rx="9" :fill="faceScreen" :stroke="accentCyan" stroke-width="2" />
              <g :class="eyesPulseClass">
                <circle cx="93" cy="88" r="3.9" :fill="accentCyan" />
                <circle cx="107" cy="88" r="3.9" :fill="accentCyan" />
              </g>
              <path d="M92 97h16" :stroke="accentCyan" stroke-width="2.5" stroke-linecap="round" />
              <rect x="58" y="78" width="4.8" height="30" rx="2" :fill="displayColor" />
              <rect x="137.2" y="78" width="4.8" height="30" rx="2" :fill="displayColor" />

              <line x1="100" y1="60" x2="100" y2="40" :stroke="accentCyan" stroke-width="2.5" />

              <circle cx="100" cy="37" r="6.5" :fill="accentCyan" :stroke="ink" stroke-width="2" />
            </g>

            <path d="M61 170c9-15 23-24 39-24s30 10 39 24" :fill="displayColor" :stroke="ink" stroke-width="2" />
          </g>
        </g>

        <!-- Fallback = orbit -->
        <g v-else>
          <rect x="9.5" y="19.5" width="6" height="10.5" rx="1.2" :fill="displayColor" />
          <rect x="48.5" y="19.5" width="6" height="10.5" rx="1.2" :fill="displayColor" />
          <circle cx="32" cy="23.5" r="14" :fill="displayColor" />
          <line x1="32" y1="9.5" x2="32" y2="3.5" />
          <circle cx="32" cy="2.4" r="2" :fill="displayColor" />
          <circle
            cx="32"
            cy="23.5"
            r="9.85"
            :fill="faceScreen"
            :stroke="accentCyan"
            stroke-width="0.9"
          />
          <g :class="eyesPulseClass">
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="24.85,21.85 26.25,20.65 28.15,20.65 29.55,21.85 28.15,23.05 26.25,23.05"
            />
            <polygon
              :fill="accentCyan"
              stroke="none"
              points="34.45,21.85 35.85,20.65 37.75,20.65 39.15,21.85 37.75,23.05 35.85,23.05"
            />
          </g>
          <rect x="29.35" y="27.55" width="5.3" height="1.75" rx="0.35" :fill="accentCyan" stroke="none" />
          <rect x="27.5" y="36.2" width="9" height="3" rx="1" :fill="displayColor" />
          <path
            transform="translate(0, 10)"
            d="M 10.8 41.5 L 53.2 41.5 A 21.2 11.5 0 0 0 10.8 41.5 Z"
            :fill="displayColor"
          />
        </g>
      </g>
    </svg>
  </div>
</template>

<script>
import {
  normalizeAgentAvatarVariant,
  normalizeAgentAvatarColor,
  DEFAULT_AGENT_AVATAR_VARIANT,
  DEFAULT_AGENT_AVATAR_COLOR
} from '../constants/agentAvatarPresets.js'

function hexToRgba(hex, alpha) {
  const h = String(hex || '').replace('#', '')
  if (h.length !== 6) return `rgba(99, 102, 241, ${alpha})`
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

/** Contorno do robô (flat). */
const INK = '#171717'

/** Tela/painel facial escuro (fixo; só o corpo usa a cor do agente). */
const FACE_SCREEN = '#0b1020'
/** Olhos e boca estilo LED — ciano vivo (fixo). */
const ACCENT_CYAN = '#22d3ee'

export default {
  name: 'AgentAvatar',
  props: {
    variant: {
      type: String,
      default: DEFAULT_AGENT_AVATAR_VARIANT
    },
    color: {
      type: String,
      default: DEFAULT_AGENT_AVATAR_COLOR
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['xs', 'sm', 'md', 'lg', 'xl', 'form', 'dashboard', 'picker'].includes(v)
    },
    name: {
      type: String,
      default: ''
    },
    /** Quando true, só os olhos (LED) fazem pulse — item selecionado na grelha do picker. */
    selected: {
      type: Boolean,
      default: false
    },
    /** Mesmo pulse nos olhos — ex.: agente com execução ativa no painel de execuções. */
    eyesPulse: {
      type: Boolean,
      default: false
    },
    /** Estrela “coordenador” no canto — esconder quando o contexto já deixa isso óbvio (ex.: lista só de coordenadores no chat). */
    showCoordinatorStar: {
      type: Boolean,
      default: true
    },
    /** Anel em volta do avatar: branco (padrão) ou ciano (ex.: chat). */
    ringCyan: {
      type: Boolean,
      default: false
    },
    /** Com ring ciano: halo mais junto ao anel (menos blur / fundo do disco mais discreto). Ex.: mapa da rede. */
    ringCyanTightGlow: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isCoordinator() {
      return String(this.effectiveVariant || '').startsWith('coord_')
    },
    ringClass() {
      if (this.ringCyan) {
        if (this.ringCyanTightGlow) {
          /* Halo bem colado ao anel: leve “aceso” sem o spread antigo. */
          return 'border border-cyan-400 shadow-[0_0_4px_rgba(34,211,238,0.58),0_0_10px_rgba(34,211,238,0.14)]'
        }
        return 'border border-cyan-400 shadow-[0_0_16px_rgba(34,211,238,0.32)]'
      }
      return 'ring-2 ring-white shadow-sm'
    },
    ink() {
      return INK
    },
    effectiveVariant() {
      return normalizeAgentAvatarVariant(this.variant)
    },
    effectiveColor() {
      return normalizeAgentAvatarColor(this.color)
    },
    displayColor() {
      return this.effectiveColor
    },
    faceScreen() {
      return FACE_SCREEN
    },
    accentCyan() {
      return ACCENT_CYAN
    },
    eyesPulseClass() {
      return {
        'agent-avatar-eyes-pulse': this.selected || this.eyesPulse
      }
    },
    wrapperStyle() {
      if (this.ringCyan && this.ringCyanTightGlow) {
        return { backgroundColor: 'transparent' }
      }
      return {
        backgroundColor: hexToRgba(this.effectiveColor, 0.14)
      }
    },
    ariaLabel() {
      const n = (this.name || '').trim()
      return n ? `Avatar do agente ${n}` : 'Avatar do agente'
    },
    sizeClasses() {
      const map = {
        xs: { wrapper: 'w-12 h-12', svg: 'w-8 h-8' },
        sm: { wrapper: 'w-10 h-10', svg: 'w-7 h-7' },
        md: { wrapper: 'w-14 h-14', svg: 'w-10 h-10' },
        lg: { wrapper: 'w-16 h-16', svg: 'w-11 h-11' },
        // Painel de execuções: maior que lg, um pouco abaixo de xl
        dashboard: { wrapper: 'w-[4.75rem] h-[4.75rem]', svg: 'w-[3.375rem] h-[3.375rem]' },
        // Formulário / destaque (~80px)
        xl: { wrapper: 'w-20 h-20', svg: 'w-14 h-14' },
        // Preview grande no modal criar/editar agente (~96px)
        form: { wrapper: 'w-24 h-24', svg: 'w-16 h-16' },
        // Grelha do modal de escolha: preenche melhor o quadrado (~84px círculo, SVG ~70px)
        picker: { wrapper: 'w-[5.25rem] h-[5.25rem]', svg: 'w-[4.375rem] h-[4.375rem]' }
      }
      const base = map[this.size] || map.md
      const star =
        this.size === 'xs'
          ? 'w-4 h-4'
          : this.size === 'sm'
            ? 'w-3.5 h-3.5'
            : this.size === 'md'
              ? 'w-4 h-4'
              : this.size === 'lg'
                ? 'w-[1.15rem] h-[1.15rem]'
                : this.size === 'dashboard'
                  ? 'w-5 h-5'
                  : this.size === 'form'
                    ? 'w-6 h-6'
                    : 'w-5 h-5'
      return { ...base, star }
    }
  }
}
</script>

<style scoped>
/* Pulse nos olhos (LED): props selected (picker) ou eyesPulse (execução ativa) */
.agent-avatar-eyes-pulse {
  animation: agent-avatar-eyes-pulse 2.2s ease-in-out infinite;
}

@media (prefers-reduced-motion: reduce) {
  .agent-avatar-eyes-pulse {
    animation: none;
  }
}

@keyframes agent-avatar-eyes-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.38;
  }
}
</style>
